import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, ViewChild, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { PanelModule } from 'primeng/panel';
import { TagModule } from 'primeng/tag';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService, MenuItem } from 'primeng/api';
import { ControlType, FileControlValue, LineItemModel, LineItemTableColumnModel, ProjectTemplateSkidStatusModel, TemplateSectionModel } from '../../models/template.model';
import { Subject, forkJoin, of, firstValueFrom } from 'rxjs';
import { takeUntil, debounceTime, catchError } from 'rxjs/operators';
import { ChecklistStorageService } from '../../services/checklist-storage.service';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
// Import all control components
import { TextboxControlComponent } from './components/controls/textbox-control.component';
import { TextareaControlComponent } from './components/controls/textarea-control.component';
import { CheckboxControlComponent } from './components/controls/checkbox-control.component';
import { SelectControlComponent } from './components/controls/select-control.component';
import { TableControlComponent } from './components/controls/table-control.component';
import { DatepickerControlComponent } from './components/controls/datepicker-control.component';
import { DatetimepickerControlComponent } from './components/controls/datetimepicker-control.component';
import { AttachmentControlComponent } from './components/controls/attachment-control.component';
import { PictureControlComponent } from './components/controls/picture-control.component';
import { SignatureControlComponent } from './components/controls/signature-control.component';
import { DigitalsignatureControlComponent } from './components/controls/digitalsignature-control.component';
import { QrscannerControlComponent } from './components/controls/qrscanner-control.component';
import { WeatherControlComponent } from './components/controls/weather-control.component';
import { RichtextControlComponent } from './components/controls/richtext-control.component';
import { LabeltextControlComponent } from './components/controls/labeltext-control.component';
import { TasksControlComponent } from './components/controls/tasks-control.component';
import { IntegerControlComponent } from './components/controls/integer-control.component';
import { DecimalControlComponent } from './components/controls/decimal-control.component';
import { UsersControlComponent } from './components/controls/users-control.component';
import { JudgementControlComponent } from './components/controls/judgement-control.component';
import { SkidsControlComponent } from './components/controls/skids-control.component';
import { ProjecttemplateService } from '../../services/projecttemplate.service';
import { ProjectModel, ProjectTemplateModel, ProjectTemplateStatus } from '../../models/project.model';
import { Select } from "primeng/select";
import { MenuModule } from 'primeng/menu';
import { Menu } from 'primeng/menu';
import { UserService } from '../../services/user.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectService } from '../../services/project.service';
import { ProjectFormService } from '../../services/projectform.service';
import { TaskFormComponent } from '../../shared/components/task/task-form.component';
import { TaskManagementService } from '../../services/task-management.service';
import { TimezoneService } from '../../services/timezone.service';
import { ProjectTask, TaskCreateUpdate, TaskFilter } from '../../models/task-management.models';
import { ProjectStateService } from '../../services/project-state.service';
import {
    buildEquipmentChecklistPdfFileName,
    buildProjectFormPdfFileName,
} from '../../utils/download-file-name.utils';
import { PermissionHelperService } from '../../services/permission-helper.service';
import { PAGE_KEYS, PERMISSION_KEYS } from '../../constants/permission.constants';
import { IntegrationService } from '../../services/integration.service';
import { IntegrationSourceExtractorService } from '../../services/integration-source-extractor.service';
import { IntegrationMappingModel } from '../../models/integration.model';
import { ValidationService } from '../../services/validation.service';
import { MesSerialNumbersModalComponent } from './components/mes-serial-numbers-modal.component';
import { PendingChangesComponent } from '../../core/guards/pending-changes.guard';

type TemplateAccessState = 'editable' | 'readonly' | 'locked';
type SaveCompletionMode = 'navigate' | 'stay';

@Component({
    selector: 'app-skidchecksheetenhance',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        CardModule,
        PanelModule,
        TagModule,
        DatePickerModule,
        TabsModule,
        TooltipModule,
        MenuModule,
        DialogModule,
        // Control components
        TextboxControlComponent,
        TextareaControlComponent,
        CheckboxControlComponent,
        SelectControlComponent,
        TableControlComponent,
        DatepickerControlComponent,
        DatetimepickerControlComponent,
        AttachmentControlComponent,
        PictureControlComponent,
        SignatureControlComponent,
        DigitalsignatureControlComponent,
        QrscannerControlComponent,
        WeatherControlComponent,
        RichtextControlComponent,
        LabeltextControlComponent,
        TasksControlComponent,
        IntegerControlComponent,
        DecimalControlComponent,
        UsersControlComponent,
        SkidsControlComponent,
        JudgementControlComponent,
        Select,
        TaskFormComponent,
        MesSerialNumbersModalComponent
    ],
    templateUrl: './skidchecksheetenhance.html',
    styleUrls: ['./skidchecksheetenhance.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SkidchecksheetenhanceComponent implements OnInit, OnDestroy, PendingChangesComponent {
    skidData: any;
    projectId: number = 0;
    blockScheduleId: number = 0;
    projectFormId: number = 0;
    isProjectFormMode: boolean = false;
    currentProjectForm: any = null; // Store current project form data including templateNameId
    projectData: any = null; // Store project data (includes customerCity, customerState)
    currentProject: ProjectModel | null = null;
    templates: ProjectTemplateModel[] = [];
    loading: boolean = true;
    saving: boolean = false;
    downloading: boolean = false;
    isReadOnly: boolean = false;
    canViewEquipment: boolean = false;
    canEditEquipment: boolean = false;
    canViewTasks: boolean = false;
    canCreateTasks: boolean = false;
    canUpdateTasks: boolean = false;
    canOverrideStatus: boolean = false;
    canViewMesData: boolean = false;
    isProjectInactive: boolean = false;
    activeTabIndex: number = 0;
    activeTabValue: string = '0';
    requestedTemplateId: number | null = null;
    templateStatusList: any[] = ProjectTemplateStatus ? Object.keys(ProjectTemplateStatus)
        .filter(key => isNaN(Number(key))) // Filter out numeric keys
        .map(key => ({
            label: key,
            value: ProjectTemplateStatus[key as keyof typeof ProjectTemplateStatus]
        })) : [];
    // For tracking form data - using lineItemId as key
    checklistData: { [lineItemId: number]: any } = {};

    // For tracking response IDs after save (lineItemId -> responseId)
    responseIdMap: { [lineItemId: number]: number } = {};

    // For tracking existing file data for file controls
    existingFilesMap: { [lineItemId: number]: any[] } = {};
    integrationSourceByMappingId: { [mappingId: number]: string } = {};

    // Control type enum for template
    ControlType = ControlType;

    /** Set to true after a failed save attempt to trigger validation error display on all controls */
    showErrors = false;

    // Template Skid Status
    templateStatusData: { [templateId: number]: ProjectTemplateSkidStatusModel } = {};
    statusFieldSelection: { [templateId: number]: number | null | undefined } = {};
    statusFieldVisible: { [templateId: number]: boolean } = {};
    templateAccessState: { [templateId: number]: TemplateAccessState } = {};
    overrideSelection: { [templateId: number]: boolean } = {};
    loadedTemplateStatusIds = new Set<number>();
    statusOptions = [
        { label: 'Draft', value: ProjectTemplateStatus.Draft },
        { label: 'Submitted', value: ProjectTemplateStatus.Submitted },
        { label: 'Verified', value: ProjectTemplateStatus.Verified }
    ];
    projectUsers: any[] = [];

    // Update menu items for the Update and Close button
    updateMenuItems: any[] = [];
    private pendingImpactedAreaNotificationTemplateId: number | null = null;
    private saveCompletionMode: SaveCompletionMode = 'navigate';

    // MES Serial Numbers modal
    showMesModal = false;

    // Reference to the menu component
    @ViewChild('menu') menu!: Menu;

    // Reference to the tabs scroll container
    @ViewChild('tabsScroll') set tabsScrollRef(el: ElementRef<HTMLElement>) {
        if (el) {
            this.initDragScroll(el.nativeElement);
        }
    }

    // Task Management
    showTaskFormDialog: boolean = false;
    showViewTasksDialog: boolean = false;
    selectedSectionForTask: any = null;
    selectedTemplateForTask: ProjectTemplateModel | null = null;
    sectionTasks: ProjectTask[] = [];
    loadingTasks: boolean = false;
    taskMenuItems: MenuItem[] = [];
    editTaskForDialog: ProjectTask | undefined = undefined;

    // Task Counts per section: key = sectionId_templateId
    sectionTaskCounts: { [key: string]: { total: number; open: number; completed: number } } = {};
    allTasks: ProjectTask[] = [];

    private destroy$ = new Subject<void>();
    private saveToStorageSubject$ = new Subject<number>();
    private dirtyTemplateIds = new Set<number>();
    private dirtyLineItemIds = new Set<number>();

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private projectService: ProjectService,
        private projecttemplateService: ProjecttemplateService,
        private projectFormService: ProjectFormService,
        private messageService: MessageService,
        private cdr: ChangeDetectorRef,
        private storageService: ChecklistStorageService,
        private userService: UserService,
        private taskManagementService: TaskManagementService,
        private timezoneService: TimezoneService,
        private projectStateService: ProjectStateService,
        private permissionHelper: PermissionHelperService,
        private integrationService: IntegrationService,
        private integrationSourceExtractor: IntegrationSourceExtractorService,
        private validationService: ValidationService
    ) {}

    ngOnInit(): void {
        // Extract projectId and blockScheduleId or projectFormId from URL parameters
        this.projectStateService.currentProject$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(project => {
            this.currentProject = project;
            this.initializeUpdateMenu();
            this.cdr.markForCheck();
        });

        this.route.queryParamMap.pipe(
            takeUntil(this.destroy$)
        ).subscribe(queryParams => {
            const templateIdParam = queryParams.get('templateId');
            this.requestedTemplateId = templateIdParam ? parseInt(templateIdParam, 10) : null;

            if (this.requestedTemplateId && this.templates.length > 0) {
                this.setActiveTabByTemplateId(this.requestedTemplateId);
            }
        });

        this.route.paramMap.subscribe(params => {
            const projectIdParam = params.get('projectId');
            const blockScheduleIdParam = params.get('blockScheduleId');
            const projectFormIdParam = params.get('projectFormId');

            if (projectIdParam) {
                this.projectId = parseInt(projectIdParam);
            }

            // Determine mode based on which parameter is present
            if (projectFormIdParam) {
                this.projectFormId = parseInt(projectFormIdParam);
                this.isProjectFormMode = true;
            } else if (blockScheduleIdParam) {
                this.blockScheduleId = parseInt(blockScheduleIdParam);
                this.isProjectFormMode = false;
            }

            console.log('Skid Checksheet Enhanced initialized with:', {
                projectId: this.projectId,
                blockScheduleId: this.blockScheduleId,
                projectFormId: this.projectFormId,
                isProjectFormMode: this.isProjectFormMode
            });

            if (!this.projectId || (!this.blockScheduleId && !this.projectFormId)) {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Project ID and Block Schedule ID or Project Form ID are required'
                });
                this.router.navigate(['/equipment', this.projectId]);
                return;
            }

            // Load project data (includes customerCity, customerState)
            this.loadProjectData();

            // Load skid data from the project service
            this.loadSkidData();

            // Set up debounced localStorage save (500ms delay)
            this.saveToStorageSubject$.pipe(
                debounceTime(500),
                takeUntil(this.destroy$)
            ).subscribe(templateId => {
                this.saveCurrentTemplateToStorage(templateId);
            });

            // Initialize update menu items with status options
            this.initializeUpdateMenu();
        });

        this.projectStateService.currentProjectUserActive$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(isActive => {
            this.isProjectInactive = isActive === false;
            this.updateReadOnlyState();
            this.initializeUpdateMenu();
            this.cdr.markForCheck();
        });

        this.projectStateService.currentProjectPermissions$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.canViewEquipment = this.permissionHelper.has(PAGE_KEYS.EQUIPMENT, PERMISSION_KEYS.VIEW);
            this.canEditEquipment = this.permissionHelper.has(PAGE_KEYS.EQUIPMENT, PERMISSION_KEYS.UPDATE);
            this.canOverrideStatus = this.permissionHelper.has(PAGE_KEYS.EQUIPMENT, PERMISSION_KEYS.OVERRIDE_STATUS);
            this.canViewMesData = this.permissionHelper.has(PAGE_KEYS.EQUIPMENT, PERMISSION_KEYS.MES_DATA);
            this.canViewTasks = this.permissionHelper.has(PAGE_KEYS.TASKS, PERMISSION_KEYS.VIEW);
            this.canCreateTasks = this.permissionHelper.has(PAGE_KEYS.TASKS, PERMISSION_KEYS.CREATE);
            this.canUpdateTasks = this.permissionHelper.has(PAGE_KEYS.TASKS, PERMISSION_KEYS.UPDATE);
            this.updateReadOnlyState();
            this.initializeUpdateMenu();
            this.cdr.markForCheck();
        });
    }

    private updateReadOnlyState(): void {
        this.isReadOnly = this.isProjectInactive || !this.canEditEquipment;
    }

    private initializeUpdateMenu(): void {
        const activeTemplate = this.templates[this.activeTabIndex];
        const canUpdateTemplate = !!activeTemplate && this.canUseUpdateAndClose(activeTemplate.templateId);
        const validationErrors = activeTemplate ? this.validateForm(activeTemplate.templateId) : [];
        const isFormValid = validationErrors.length === 0;
        const footerStatusOptions = this.getFooterStatusOptions(activeTemplate?.templateId);

        this.updateMenuItems = [
            {
                label: 'Status',
                items: footerStatusOptions.map(option => ({
                label: option.label,
                disabled: !canUpdateTemplate || (this.statusRequiresValidation(option.value) && !isFormValid),
                command: () => {
                    this.onStatusSelectedAndSave(option.value);
                }
                }))
            }
        ];
    }

    // Method to handle Update and Close button click
    onUpdateAndCloseClick(event: Event): void {
        const activeTemplate = this.templates[this.activeTabIndex];
        if (!activeTemplate || !this.canUseUpdateAndClose(activeTemplate.templateId)) {
            return;
        }
        // Refresh menu items to update disabled states based on current form validity
        this.initializeUpdateMenu();
        this.cdr.markForCheck();
        // Open the menu
        if (this.menu) {
            this.menu.toggle(event);
        }
    }

    private loadSkidData(): void {
        if (this.isProjectFormMode) {
            // In project form mode, we don't need to load block schedule data
            // The skidData will be populated from project form data later
            this.skidData = {
                projectFormId: this.projectFormId,
                isFormMode: true
            };
            this.cdr.markForCheck();
            // Load checklist data after skid data is ready
            this.loadChecklistData();
        } else {
            // Block schedule mode - load skid data
            this.projectService.getProjectBlockSchedule(this.projectId).pipe(
                takeUntil(this.destroy$)
            ).subscribe({
                next: (skids: any) => {
                    this.skidData = skids.find((s: any) => s.blockScheduleId === this.blockScheduleId);
                    if (!this.skidData) {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'MV Power Station not found'
                        });
                        this.router.navigate(['/equipment', this.projectId]);
                        return;
                    }
                    this.cdr.markForCheck();
                    // Load checklist data after skid data is ready
                    this.loadChecklistData();
                },
                error: (error: any) => {
                    console.error('Error loading skid data:', error);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to load MV Power Station data'
                    });
                    this.router.navigate(['/equipment', this.projectId]);
                }
            });
        }
    }

    private loadProjectData(): void {
        // Load project data to get customerCity and customerState
        this.projectService.getProjectById(this.projectId).pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: (project) => {
                this.projectData = project;
                console.log('Project data loaded:', project);
                this.cdr.markForCheck();
            },
            error: (error) => {
                console.error('Error loading project data:', error);
                // Don't show error to user, weather control will handle missing data gracefully
            }
        });
    }

    private initDragScroll(el: HTMLElement): void {
        let isDown = false;
        let startX = 0;
        let scrollLeft = 0;
        let hasDragged = false;

        el.addEventListener('mousedown', (e: MouseEvent) => {
            isDown = true;
            hasDragged = false;
            startX = e.pageX - el.offsetLeft;
            scrollLeft = el.scrollLeft;
        });

        el.addEventListener('mouseleave', () => {
            isDown = false;
            el.style.cursor = 'grab';
        });

        el.addEventListener('mouseup', () => {
            isDown = false;
            el.style.cursor = 'grab';
        });

        el.addEventListener('mousemove', (e: MouseEvent) => {
            if (!isDown) return;
            const x = e.pageX - el.offsetLeft;
            const walk = x - startX;
            if (!hasDragged && Math.abs(walk) < 5) return; // ignore tiny jitter
            e.preventDefault();
            hasDragged = true;
            el.style.cursor = 'grabbing';
            el.scrollLeft = scrollLeft - walk * 1.5;
        });

        // Block the click on any child button if the user was dragging
        el.addEventListener('click', (e: MouseEvent) => {
            if (hasDragged) {
                e.stopPropagation();
                hasDragged = false;
            }
        }, true);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload(event: BeforeUnloadEvent): void {
        if (!this.hasUnsavedChanges()) {
            return;
        }

        event.preventDefault();
        event.returnValue = '';
    }

    hasUnsavedChanges(): boolean {
        return !this.isReadOnly && this.dirtyTemplateIds.size > 0;
    }

    discardUnsavedChanges(): void {
        this.dirtyTemplateIds.clear();
        this.dirtyLineItemIds.clear();
    }

    async saveUnsavedChangesAsDraft(): Promise<boolean> {
        const templateIds = Array.from(this.dirtyTemplateIds);
        if (templateIds.length === 0) {
            return true;
        }

        this.saving = true;
        this.cdr.markForCheck();

        try {
            let recordsSaved = 0;
            let totalFiles = 0;
            let uploadedFiles = 0;
            let failedFiles = 0;

            for (const templateId of templateIds) {
                const request = this.prepareSaveRequest(templateId);
                if (!request) {
                    continue;
                }

                const response = await firstValueFrom(this.projecttemplateService.saveSkidChecklistResponses(request));
                recordsSaved += response?.recordsSaved || 0;

                if (response?.responseIdMap) {
                    this.responseIdMap = { ...this.responseIdMap, ...response.responseIdMap };
                }

                const uploadResult = await this.uploadFiles(templateId);
                totalFiles += uploadResult.totalFiles;
                uploadedFiles += uploadResult.uploadedFiles;
                failedFiles += uploadResult.failedFiles;

                if (uploadResult.failedFiles > 0) {
                    throw new Error('One or more file uploads failed.');
                }

                this.clearStoredDraft(templateId);
                this.dirtyTemplateIds.delete(templateId);
                this.clearDirtyLineItems(request.responses);
            }

            this.saving = false;
            this.cdr.markForCheck();
            this.messageService.add({
                severity: 'success',
                summary: 'Draft Saved',
                detail: `Draft saved successfully. ${recordsSaved} record(s) saved.${totalFiles > 0 ? ` ${uploadedFiles} file(s) uploaded.` : ''}`
            });
            return true;
        } catch (error) {
            this.saving = false;
            this.cdr.markForCheck();
            console.error('Error saving draft before navigation:', error);
            this.messageService.add({
                severity: 'error',
                summary: 'Draft Save Failed',
                detail: 'Unable to save the draft. Please try again or choose Leave Without Saving.'
            });
            return false;
        }
    }

    goBack(): void {
        if (this.isProjectFormMode && this.currentProjectForm) {
            // Navigate back to form-detail for the specific folder
            const folderId = this.currentProjectForm.formFolderId;
            if (folderId && this.projectId) {
                this.router.navigate(['/form-detail', this.projectId, folderId]);
            } else {
                window.history.back();
            }
        } else {
            // For block schedule mode, navigate back to equipment list
            this.router.navigate(['/equipment', this.projectId]);
        }
    }

    openMesModal(): void {
        if (!this.canViewMesData) {
            return;
        }
        this.showMesModal = true;
        this.cdr.markForCheck();
    }

    private loadChecklistData(): void {
        this.loading = true;
        this.cdr.markForCheck();

        if (this.isProjectFormMode) {
            // Project Form Mode: Fetch project form data first, then load the specific template
            this.projectFormService.getProjectFormById(this.projectFormId).pipe(
                takeUntil(this.destroy$)
            ).subscribe({
                next: (projectForm) => {
                    console.log('Project form loaded:', projectForm);

                    // Store the project form data for later use (includes templateNameId)
                    this.currentProjectForm = projectForm;

                    if (!projectForm.projectTemplateId) {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Project template ID not found'
                        });
                        this.loading = false;
                        this.cdr.markForCheck();
                        return;
                    }

                    // Load the specific template by ID with full hierarchy (sections, line items, etc.)
                    this.projecttemplateService.getProjectTemplateDataByTemplateId(projectForm.projectTemplateId).pipe(
                        takeUntil(this.destroy$)
                    ).subscribe({
                        next: (template) => {
                            console.log('Template loaded for project form:', template);

                            // Validate that the template is typeId = 2 (Form type)
                            if (template.typeId !== 2) {
                                console.warn(`Project form template has typeId ${template.typeId}, expected 2 (Form type)`);
                            }

                    this.templates = [template];
                    this.loadedTemplateStatusIds.clear();
                    this.templates.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
                    this.templates.forEach(x => {
                        x.dueDate = x.dueDate != null ? new Date(x.dueDate) : new Date();
                                // Build hierarchy for sections
                                if (x.sections && x.sections.length > 0) {
                                    x.sections = this.buildHierarchy(x.sections);
                                    this.sortLineItems(x.sections);
                                }
                            });

                            this.syncTemplateAccessState();
                            this.applyRequestedActiveTab();

                            this.cdr.markForCheck();

                            // Load project users for assignee dropdown
                            this.loadProjectUsers();
                            this.loadIntegrationSourceMappings();

                            // Load template status (from project_form table for forms mode)
                            this.loadTemplateStatus(template.templateId);

                            // Load existing responses
                            this.loadExistingResponses();
                        },
                        error: (error) => {
                            console.error('Error loading template data:', error);
                            this.messageService.add({
                                severity: 'error',
                                summary: 'Error',
                                detail: 'Failed to load template data'
                            });
                            this.loading = false;
                            this.cdr.markForCheck();
                        }
                    });
                },
                error: (error) => {
                    console.error('Error loading project form data:', error);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to load project form data'
                    });
                    this.loading = false;
                    this.cdr.markForCheck();
                }
            });
        } else {
            // Block Schedule Mode: Load role-filtered templates with full hierarchy
            this.projecttemplateService.getProjectTemplatesDataByUserRoleAndSkid(this.projectId, 1, this.blockScheduleId).pipe(
                takeUntil(this.destroy$)
            ).subscribe({
                next: (data) => {
                    console.log('Checklist templates loaded:', data);

                    this.templates = data || [];
                    this.loadedTemplateStatusIds.clear();
                    this.templates.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
                    this.templates.forEach(x => {
                        x.dueDate = x.dueDate != null ? new Date(x.dueDate) : new Date();
                        // Build hierarchy for sections
                        if (x.sections && x.sections.length > 0) {
                            x.sections = this.buildHierarchy(x.sections);
                            this.sortLineItems(x.sections);
                        }
                    });

                    this.syncTemplateAccessState();
                    this.applyRequestedActiveTab();

                    this.cdr.markForCheck();

                    // Load project users for assignee dropdown
                    this.loadProjectUsers();
                    this.loadIntegrationSourceMappings();

                    // Load task counts for all sections
                    this.loadSectionTaskCounts();

                    // Load template status for each template
                    this.templates.forEach(template => {
                        if (template.templateId) {
                            this.loadTemplateStatus(template.templateId);
                        }
                    });

                    // After templates are loaded, load existing responses if any
                    this.loadExistingResponses();
                },
                error: (error) => {
                    console.error('Error loading checklist data:', error);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to load checklist data'
                    });
                    this.loading = false;
                    this.cdr.markForCheck();
                }
            });
        }
    }

    private loadIntegrationSourceMappings(): void {
        forkJoin([
            this.integrationService.getIntegrationMappings('Kisha').pipe(
                catchError(() => of([] as IntegrationMappingModel[]))
            ),
            this.integrationService.getIntegrationMappings('EWMR').pipe(
                catchError(() => of([] as IntegrationMappingModel[]))
            )
        ]).pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: ([kishaMappings, ewmrMappings]) => {
                const lookup: { [mappingId: number]: string } = {};
                const combinedMappings = [...(kishaMappings || []), ...(ewmrMappings || [])];
                combinedMappings.forEach(mapping => {
                    if (mapping.integrationId && mapping.sourceId) {
                        lookup[mapping.integrationId] = mapping.sourceId;
                    }
                });
                this.integrationSourceByMappingId = lookup;
            },
            error: (error) => {
                console.error('Error loading integration source mappings:', error);
                this.integrationSourceByMappingId = {};
            }
        });
    }

    private loadExistingResponses(): void {
        console.log('loadExistingResponses called with:', {
            isProjectFormMode: this.isProjectFormMode,
            projectFormId: this.projectFormId,
            projectId: this.projectId,
            skidData: this.skidData,
            blockScheduleId: this.skidData?.blockScheduleId
        });

        if (this.isProjectFormMode) {
            // Project Form Mode - load responses by projectFormId
            this.projecttemplateService.getSkidChecklistResponsesByProjectFormId(this.projectFormId, this.projectId).pipe(
                takeUntil(this.destroy$)
            ).subscribe({
                next: (responses) => {
                    console.log('Existing project form responses loaded from backend:', responses);
                    this.cdr.markForCheck();
                    // Build responseIdMap from loaded responses
                    responses.forEach(response => {
                        this.responseIdMap[response.lineitemId] = response.id;

                        if (response.responseValue) {
                            try {
                                // Try to parse as JSON (for complex types like arrays, objects)
                                const parsedValue = JSON.parse(response.responseValue);
                                this.checklistData[response.lineitemId] = parsedValue;
                            } catch {
                                // If not JSON, use as-is (for simple strings, numbers, etc.)
                                this.checklistData[response.lineitemId] = response.responseValue;
                            }
                        }
                    });

                    this.mergeLocalStorageData(this.projectFormId);

                    // Load existing files for attachment/picture controls
                    // Note: For project forms, we use projectFormId instead of blockScheduleId
                    this.loadExistingFiles(this.projectFormId);
                },
                error: (error) => {
                    console.error('Error loading existing project form responses:', error);
                    this.loading = false;
                    this.cdr.markForCheck();
                }
            });
        } else {
            // Block Schedule Mode - load responses by blockScheduleId
            const blockScheduleId = this.skidData?.blockScheduleId;

            if (!blockScheduleId) {
                console.warn('GetSkidChecklistResponses API call SKIPPED - blockScheduleId is null/undefined:', {
                    skidData: this.skidData,
                    blockScheduleId: blockScheduleId,
                    originalBlockScheduleId: this.blockScheduleId
                });
                // Even without backend data, check localStorage
                this.loadAllTemplatesFromStorage();
                this.loading = false;
                this.cdr.markForCheck();
                return;
            }

            console.log('Making GetSkidChecklistResponses API call with:', {
                blockScheduleId: blockScheduleId,
                projectId: this.projectId
            });

            this.projecttemplateService.getSkidChecklistResponses(blockScheduleId, this.projectId).pipe(
                takeUntil(this.destroy$)
            ).subscribe({
                next: (responses) => {
                    console.log('Existing checklist responses loaded from backend:', responses);
                    this.cdr.markForCheck();
                    // Build responseIdMap from loaded responses
                    responses.forEach(response => {
                        this.responseIdMap[response.lineitemId] = response.id;

                        if (response.responseValue) {
                            try {
                                // Try to parse as JSON (for complex types like arrays, objects)
                                const parsedValue = JSON.parse(response.responseValue);
                                this.checklistData[response.lineitemId] = parsedValue;
                            } catch {
                                // If not JSON, use as-is (for simple strings, numbers, etc.)
                                this.checklistData[response.lineitemId] = response.responseValue;
                            }
                        }
                    });

                    // Now merge with localStorage data (backend takes priority if data exists)
                    this.mergeLocalStorageData(blockScheduleId);

                    // Load existing files for attachment/picture controls
                    this.loadExistingFiles(blockScheduleId);
                },
                error: (error) => {
                    console.error('Error loading existing responses:', error);
                    // Don't show error to user - load from localStorage if available
                    this.loadAllTemplatesFromStorage();
                    this.loading = false;
                    this.cdr.markForCheck();
                }
            });
        }
    }

    private loadAllTemplatesFromStorage(): void {
        const storageContextId = this.getStorageContextId();
        if (!storageContextId) return;

        this.templates.forEach(template => {
            const localData = this.storageService.loadTemplateData(
                this.projectId,
                storageContextId,
                template.templateId
            );

            if (localData) {
                console.log(`Loading template ${template.templateId} from localStorage (no backend data)`);
                Object.assign(this.checklistData, localData.data);
            }
        });
    }

    private mergeLocalStorageData(blockScheduleId: number): void {
        this.templates.forEach(template => {
            const localData = this.storageService.loadTemplateData(
                this.projectId,
                blockScheduleId,
                template.templateId
            );

            if (localData) {
                // Merge draft data conservatively:
                // keep server-fetched values when present to avoid stale local drafts
                // overriding freshly synced data from mobile/other clients.
                console.log(`Loading template ${template.templateId} from localStorage (draft changes)`);
                Object.keys(localData.data || {}).forEach((lineItemIdStr) => {
                    const lineItemId = Number(lineItemIdStr);
                    if (!Number.isFinite(lineItemId)) {
                        return;
                    }

                    const existingValue = this.checklistData[lineItemId];
                    const hasServerValue = existingValue !== undefined && existingValue !== null && existingValue !== '';
                    if (!hasServerValue) {
                        this.checklistData[lineItemId] = localData.data[lineItemId];
                    }
                });
            }
        });
    }

    /**
     * Recursively collect file control line items from sections and subsections
     */
    private collectFileControlLineItems(sections: TemplateSectionModel[], fileControlLineItems: number[]): void {
        sections.forEach(section => {
            // Collect file controls in current section
            section.lineItemList?.forEach(lineItem => {
                if ((lineItem.controlTypeId === ControlType.Attachment || lineItem.controlTypeId === ControlType.Picture || lineItem.controlTypeId === ControlType.Signature)
                    && this.responseIdMap[lineItem.lineItemId]) {
                    fileControlLineItems.push(lineItem.lineItemId);
                }
            });

            // Recursively collect from subsections
            if (section.subsections && section.subsections.length > 0) {
                this.collectFileControlLineItems(section.subsections, fileControlLineItems);
            }
        });
    }

    private loadExistingFiles(blockScheduleId: number): void {
        // Get all file controls that have response IDs
        const fileControlLineItems: number[] = [];

        this.templates.forEach(template => {
            if (template.sections && template.sections.length > 0) {
                this.collectFileControlLineItems(template.sections, fileControlLineItems);
            }
        });

        if (fileControlLineItems.length === 0) {
            this.initializeDefaultText();
            this.loading = false;
            this.cdr.markForCheck();
            return;
        }

        // Load files for all file controls
        const fileLoadTasks = fileControlLineItems.map(lineItemId => {
            const responseId = this.responseIdMap[lineItemId];
            return this.projecttemplateService.getFiles(blockScheduleId, responseId)
                .toPromise()
                .then(files => {
                    if (files && files.length > 0) {
                        this.existingFilesMap[lineItemId] = files;

                        // Update checklistData with file IDs
                        const currentValue = this.checklistData[lineItemId] as FileControlValue || { newFiles: [], existingFileIds: [] };
                        this.checklistData[lineItemId] = {
                            newFiles: currentValue.newFiles || [],
                            existingFileIds: files.map(f => f.id)
                        };
                    }
                })
                .catch(error => {
                    console.error('Error loading files for lineItem', lineItemId, error);
                });
        });

        Promise.all(fileLoadTasks).finally(() => {
            // Initialize default text for controls that don't have existing values
            this.initializeDefaultText();
            this.loading = false;
            this.cdr.markForCheck();
        });
    }

    /**
     * Recursively initialize default text for textbox and textarea controls
     */
    private initializeDefaultTextForSections(sections: TemplateSectionModel[]): void {
        sections.forEach(section => {
            section.lineItemList?.forEach(lineItem => {
                // Only for TextBox (1) and TextArea (2) controls
                if ((lineItem.controlTypeId === 1 || lineItem.controlTypeId === 2) &&
                    lineItem.defaultText &&
                    lineItem.lineItemId) {
                    // Only set default text if there's no existing value
                    if (this.checklistData[lineItem.lineItemId] === undefined ||
                        this.checklistData[lineItem.lineItemId] === null ||
                        this.checklistData[lineItem.lineItemId] === '') {
                        this.checklistData[lineItem.lineItemId] = lineItem.defaultText;
                    }
                }
            });

            // Recursively process subsections
            if (section.subsections && section.subsections.length > 0) {
                this.initializeDefaultTextForSections(section.subsections);
            }
        });
    }

    private initializeDefaultText(): void {
        this.templates.forEach(template => {
            if (template.sections && template.sections.length > 0) {
                this.initializeDefaultTextForSections(template.sections);
            }
        });
    }

    getLineItemValue(lineItemId: number): any {
        return this.checklistData[lineItemId];
    }

    setLineItemValue(lineItemId: number, value: any): void {
        if (this.isReadOnly) {
            return;
        }
        this.checklistData[lineItemId] = value;
        this.dirtyLineItemIds.add(lineItemId);

        // Find which template this lineItem belongs to
        const templateId = this.getTemplateIdForLineItem(lineItemId);

        if (templateId) {
            this.markTemplateDirty(templateId);
            // Trigger debounced save to localStorage
            this.saveToStorageSubject$.next(templateId);
        }

        // Trigger change detection to update button state
        this.cdr.markForCheck();
    }

    openIntegrationUpload(fileInput: HTMLInputElement): void {
        if (this.isReadOnly) {
            return;
        }
        fileInput.click();
    }

    onIntegrationFileSelected(event: Event): void {
        if (this.isReadOnly) {
            return;
        }

        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) {
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            try {
                const rawText = typeof reader.result === 'string' ? reader.result : '';
                const payload = JSON.parse(rawText);
                const updates = this.applyIntegrationPayload(payload);

                if (updates > 0) {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Applied',
                        detail: `${updates} control value(s) updated from ${file.name}.`
                    });
                } else {
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'No Matches',
                        detail: 'No mapped source_id values were found in the uploaded file.'
                    });
                }
            } catch (error) {
                console.error('Error parsing integration file:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Invalid File',
                    detail: 'Uploaded file is not a valid JSON document.'
                });
            } finally {
                input.value = '';
            }
        };

        reader.onerror = () => {
            this.messageService.add({
                severity: 'error',
                summary: 'Read Error',
                detail: 'Unable to read uploaded file.'
            });
            input.value = '';
        };

        reader.readAsText(file);
    }

    private applyIntegrationPayload(payload: unknown): number {
        let updatedCount = 0;
        this.templates.forEach(template => {
            if (template.sections?.length) {
                updatedCount += this.applyPayloadToSections(template.sections, payload);
            }
        });

        if (updatedCount > 0) {
            this.cdr.markForCheck();
        }

        return updatedCount;
    }

    private applyPayloadToSections(sections: TemplateSectionModel[], payload: unknown): number {
        let updatedCount = 0;

        sections.forEach(section => {
            section.lineItemList?.forEach(lineItem => {
                if (!lineItem.lineItemId) {
                    return;
                }

                if (lineItem.controlTypeId === ControlType.Table) {
                    const tableUpdatedCount = this.applyPayloadToTableColumns(lineItem, payload);
                    if (tableUpdatedCount > 0) {
                        updatedCount += tableUpdatedCount;
                        return;
                    }
                }

                if (!lineItem.integrationMappingId) {
                    return;
                }

                const sourceId = this.integrationSourceByMappingId[lineItem.integrationMappingId];
                if (!sourceId) {
                    return;
                }

                const extractedValue = this.integrationSourceExtractor.extractValue(sourceId, payload);
                if (!this.hasMeaningfulIntegrationValue(extractedValue)) {
                    return;
                }

                this.setLineItemValue(lineItem.lineItemId, this.normalizeValueForControl(lineItem, extractedValue));
                updatedCount++;
            });

            if (section.subsections?.length) {
                updatedCount += this.applyPayloadToSections(section.subsections, payload);
            }
        });

        return updatedCount;
    }

    private applyPayloadToTableColumns(lineItem: LineItemModel, payload: unknown): number {
        const columns = lineItem.lineItemTableColumnList || [];
        if (!columns.length || !lineItem.lineItemId) {
            return 0;
        }

        const currentValue = this.getLineItemValue(lineItem.lineItemId);
        const existingRows = Array.isArray(currentValue) ? currentValue : [];
        const baseRow =
            existingRows.length > 0 && typeof existingRows[0] === 'object' && existingRows[0] !== null
                ? { ...existingRows[0] as { [key: string]: unknown } }
                : {};
        const row: { [key: string]: unknown } = { ...baseRow };
        let mappedColumnCount = 0;

        columns.forEach((column: LineItemTableColumnModel) => {
            if (!column.columnId || !column.integrationMappingId) {
                return;
            }

            const sourceId = this.integrationSourceByMappingId[column.integrationMappingId];
            if (!sourceId) {
                return;
            }

            const extractedValue = this.integrationSourceExtractor.extractValue(sourceId, payload);
            if (!this.hasMeaningfulIntegrationValue(extractedValue)) {
                return;
            }

            row[`col_${column.columnId}`] = this.normalizeValueForTableColumn(column, extractedValue);
            mappedColumnCount++;
        });

        if (mappedColumnCount > 0) {
            this.setLineItemValue(lineItem.lineItemId, [row]);
        }

        return mappedColumnCount;
    }

    private hasMeaningfulIntegrationValue(value: unknown): boolean {
        if (value === undefined || value === null) {
            return false;
        }

        if (typeof value === 'string') {
            return value.trim().length > 0;
        }

        if (Array.isArray(value)) {
            return value.length > 0;
        }

        return true;
    }

    private normalizeValueForControl(lineItem: LineItemModel, value: unknown): unknown {
        switch (lineItem.controlTypeId) {
            case ControlType.CheckBox:
                if (typeof value === 'boolean') {
                    return value;
                }
                if (typeof value === 'number') {
                    return value !== 0;
                }
                if (typeof value === 'string') {
                    const normalized = value.trim().toLowerCase();
                    return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'y';
                }
                return !!value;
            case ControlType.Integer:
            case ControlType.Decimal:
                if (typeof value === 'number') {
                    return value;
                }
                if (typeof value === 'string') {
                    const parsed = Number(value);
                    return Number.isNaN(parsed) ? value : parsed;
                }
                return value;
            case ControlType.DatePicker:
                return this.parseDatePickerValue(value);
            case ControlType.DateTimePicker:
                if (value instanceof Date) {
                    return value;
                }
                if (typeof value === 'string' || typeof value === 'number') {
                    const parsedDate = new Date(value);
                    return Number.isNaN(parsedDate.getTime()) ? value : parsedDate;
                }
                return value;
            default:
                return value;
        }
    }

    private parseDatePickerValue(value: unknown): unknown {
        if (value instanceof Date) {
            return value;
        }

        if (typeof value === 'string') {
            const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (match) {
                return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
            }
        }

        if (typeof value === 'string' || typeof value === 'number') {
            const parsedDate = new Date(value);
            return Number.isNaN(parsedDate.getTime()) ? value : parsedDate;
        }

        return value;
    }

    private formatDateOnly(value: unknown): string | unknown {
        const date = this.parseDatePickerValue(value);
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
            return value;
        }

        const year = date.getFullYear().toString().padStart(4, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private parseStatusDate(value?: string | Date | null): Date | undefined {
        const parsed = this.parseDatePickerValue(value);
        return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : undefined;
    }

    private normalizeValueForTableColumn(column: LineItemTableColumnModel, value: unknown): unknown {
        switch (column.dataTypeId) {
            case 3: // Integer
            case 4: // Decimal
                if (typeof value === 'number') {
                    return value;
                }
                if (typeof value === 'string') {
                    const parsed = Number(value);
                    return Number.isNaN(parsed) ? value : parsed;
                }
                return value;
            case 5: // Date
            case 10: // DateTimePicker
                if (value instanceof Date) {
                    return value;
                }
                if (typeof value === 'string' || typeof value === 'number') {
                    const parsedDate = new Date(value);
                    return Number.isNaN(parsedDate.getTime()) ? value : parsedDate;
                }
                return value;
            case 6: // CheckBox
                if (typeof value === 'boolean') {
                    return value;
                }
                if (typeof value === 'number') {
                    return value !== 0;
                }
                if (typeof value === 'string') {
                    const normalized = value.trim().toLowerCase();
                    return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'y';
                }
                return !!value;
            default:
                return value;
        }
    }

    /**
     * Recursively search for a line item in sections and subsections
     */
    private findLineItemInSections(sections: TemplateSectionModel[], lineItemId: number): boolean {
        for (const section of sections) {
            // Check in current section
            for (const lineItem of section.lineItemList || []) {
                if (lineItem.lineItemId === lineItemId) {
                    return true;
                }
            }
            // Check in subsections
            if (section.subsections && section.subsections.length > 0) {
                if (this.findLineItemInSections(section.subsections, lineItemId)) {
                    return true;
                }
            }
        }
        return false;
    }

    private getTemplateIdForLineItem(lineItemId: number): number | null {
        for (const template of this.templates) {
            if (template.sections && template.sections.length > 0) {
                if (this.findLineItemInSections(template.sections, lineItemId)) {
                    return template.templateId;
                }
            }
        }
        return null;
    }

    /**
     * Recursively collect template data from sections and subsections for storage
     */
    private collectTemplateDataForStorage(sections: TemplateSectionModel[], templateData: { [lineItemId: number]: any }): void {
        sections.forEach(section => {
            // Collect line items from current section
            section.lineItemList?.forEach(lineItem => {
                const value = this.checklistData[lineItem.lineItemId];
                if (value !== undefined) {
                    templateData[lineItem.lineItemId] = value;
                }
            });

            // Recursively collect from subsections
            if (section.subsections && section.subsections.length > 0) {
                this.collectTemplateDataForStorage(section.subsections, templateData);
            }
        });
    }

    private getStorageContextId(): number | null {
        if (this.isProjectFormMode) {
            return this.projectFormId || null;
        }

        return this.skidData?.blockScheduleId || this.blockScheduleId || null;
    }

    private clearStoredDraft(templateId: number): void {
        const storageContextId = this.getStorageContextId();
        if (storageContextId) {
            this.storageService.clearTemplateData(this.projectId, storageContextId, templateId);
        }
    }

    private markTemplateDirty(templateId: number | null): void {
        if (!templateId || this.isReadOnly) {
            return;
        }

        this.dirtyTemplateIds.add(templateId);
    }

    private saveCurrentTemplateToStorage(templateId: number): void {
        const storageContextId = this.getStorageContextId();
        if (!storageContextId) return;

        // Get the template
        const template = this.templates.find(t => t.templateId === templateId);
        if (!template) return;

        // Extract only this template's line item data from all sections and subsections
        const templateData: { [lineItemId: number]: any } = {};
        if (template.sections && template.sections.length > 0) {
            this.collectTemplateDataForStorage(template.sections, templateData);
        }

        // Save to localStorage
        this.storageService.saveTemplateData(
            this.projectId,
            storageContextId,
            templateId,
            templateData
        );
    }

    onStatusSelectedAndSave(selectedStatus: number): void {
        // Get the active template
        const activeTabIdx = parseInt(this.activeTabValue);
        const activeTemplate = this.templates[activeTabIdx];

        if (!activeTemplate) {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'No active template found'
            });
            return;
        }

        if (!this.canUseUpdateAndClose(activeTemplate.templateId) || !this.getFooterStatusOptions(activeTemplate.templateId).some(option => option.value === selectedStatus)) {
            return;
        }

        if (!this.canProceedWithStatus(activeTemplate.templateId, selectedStatus)) {
            return;
        }

        // Update the status in the templateStatusData
        if (!this.templateStatusData[activeTemplate.templateId]) {
            this.templateStatusData[activeTemplate.templateId] = {
                blockScheduleId: this.isProjectFormMode ? 0 : (this.skidData?.blockScheduleId || 0),
                projectTemplateId: activeTemplate.templateId
            };
        }
        this.templateStatusData[activeTemplate.templateId].status = selectedStatus;
        this.templateStatusData[activeTemplate.templateId].isOverride = this.shouldPersistOverride(activeTemplate.templateId);

        // Call the save logic (skip validation as we already validated above if needed)
        this.onSave(true, this.shouldTriggerImpactedAreaNotification(selectedStatus), 'navigate');
    }

    onSave(skipValidation: boolean = false, shouldSendImpactedAreaNotification: boolean = false, completionMode: SaveCompletionMode = 'navigate'): void {
        // Sync activeTabIndex from activeTabValue to ensure we have the correct tab
        this.activeTabIndex = parseInt(this.activeTabValue);

        console.log('=== SAVE CLICKED ===');
        console.log('Active tab value:', this.activeTabValue);
        console.log('Active tab index (parsed):', this.activeTabIndex);
        console.log('Total templates:', this.templates.length);

        // Get the active template
        const activeTemplate = this.templates[this.activeTabIndex];
        console.log('Active template:', activeTemplate);

        if (!activeTemplate) {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'No active tab found'
            });
            return;
        }

        this.pendingImpactedAreaNotificationTemplateId = shouldSendImpactedAreaNotification ? activeTemplate.templateId : null;
        this.saveCompletionMode = completionMode;

        console.log('✓ Saving ONLY template:', activeTemplate.templateName, 'with ID:', activeTemplate.templateId);

        // Validate required fields for active tab only (unless skipValidation is true)
        if (!skipValidation) {
            const validationErrors = this.validateForm(activeTemplate.templateId);

            if (validationErrors.length > 0) {
                this.showErrors = true;
                this.cdr.markForCheck();
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Failed',
                    detail: `Please fill all required fields. ${validationErrors.length} field(s) missing.`
                });
                return;
            }
        }
        this.showErrors = false;

        this.saving = true;
        this.cdr.markForCheck();

        // Prepare the save request for active tab only
        const request = this.prepareSaveRequest(activeTemplate.templateId);

        console.log('Saving active tab data:', request);
        console.log('Number of responses being saved:', request.responses.length);

        // Phase 1: Save checklist data
        this.projecttemplateService.saveSkidChecklistResponses(request).pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: (response) => {
                console.log('Save response:', response);

                // Store response ID map for file uploads
                if (response.responseIdMap) {
                    this.responseIdMap = { ...this.responseIdMap, ...response.responseIdMap };
                }

                // Phase 2: Upload files for attachment/picture controls (active tab only)
                this.uploadFiles(activeTemplate.templateId).then((uploadResult) => {
                    const totalFiles = uploadResult.totalFiles;
                    const uploadedFiles = uploadResult.uploadedFiles;
                    const failedFiles = uploadResult.failedFiles;

                    // Phase 3: Save template status if it exists
                    const templateStatus = this.templateStatusData[activeTemplate.templateId];
                    if (templateStatus && (templateStatus.status !== undefined && templateStatus.status !== null)) {
                        this.saveTemplateStatusAndNavigate(activeTemplate.templateId, response.recordsSaved, totalFiles, uploadedFiles, failedFiles);
                    } else {
                        // No status to save, proceed to cleanup and navigate
                        this.completeSaveAndNavigate(response.recordsSaved, totalFiles, uploadedFiles, failedFiles, activeTemplate.templateId);
                    }
                }).catch((error) => {
                    this.saving = false;
                    this.pendingImpactedAreaNotificationTemplateId = null;
                    this.saveCompletionMode = 'navigate';
                    this.cdr.markForCheck();
                    console.error('Error uploading files:', error);
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Partial Success',
                        detail: 'Checklist saved but file upload failed.'
                    });
                    if (completionMode === 'navigate') {
                        if (this.isProjectFormMode) {
                            this.goBack();
                        } else {
                            this.router.navigate(['/equipment', this.projectId]);
                        }
                    }
                });
            },
            error: (error) => {
                this.saving = false;
                this.pendingImpactedAreaNotificationTemplateId = null;
                this.saveCompletionMode = 'navigate';
                this.cdr.markForCheck();
                console.error('Error saving checklist:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to save checklist data'
                });
            }
        });
    }

    /**
     * Recursively collect line item responses from sections and subsections
     */
    private collectLineItemResponses(sections: TemplateSectionModel[], responses: any[]): void {
        sections.forEach(section => {
            // Process line items in current section
            section.lineItemList?.forEach(lineItem => {
                // Skip LabelText controls - they are display-only
                if (lineItem.controlTypeId === ControlType.LabelText) {
                    return;
                }

                const value = this.checklistData[lineItem.lineItemId];
                const hasPersistedResponse = !!this.responseIdMap[lineItem.lineItemId];
                const shouldPersistInitialDefault = this.isProjectFormMode
                    && !hasPersistedResponse
                    && value !== undefined
                    && value !== null
                    && value !== '';

                if (!this.dirtyLineItemIds.has(lineItem.lineItemId) && !shouldPersistInitialDefault) {
                    return;
                }

                // Include all fields, even if empty (backend will handle storage)
                if (value !== undefined) {
                    let valueToSave = value;

                    // For file controls, save only the existingFileIds (not the File objects)
                    if (lineItem.controlTypeId === ControlType.Attachment || lineItem.controlTypeId === ControlType.Picture || lineItem.controlTypeId === ControlType.Signature) {
                        const fileValue = value as FileControlValue;
                        // Store existing file IDs for reference
                        valueToSave = {
                            existingFileIds: fileValue.existingFileIds || []
                        };
                    } else if (lineItem.controlTypeId === ControlType.DatePicker) {
                        valueToSave = this.formatDateOnly(value);
                    }

                    responses.push({
                        lineItemId: lineItem.lineItemId,
                        value: valueToSave
                    });
                }
            });

            // Recursively process subsections
            if (section.subsections && section.subsections.length > 0) {
                this.collectLineItemResponses(section.subsections, responses);
            }
        });
    }

    private prepareSaveRequest(templateId?: number): any {
        const responses: any[] = [];

        // Filter templates based on templateId parameter
        const template = this.templates.find(t => t.templateId === templateId);
        if (template == undefined) {
            return;
        }

        // Recursively collect responses from all sections and subsections
        if (template.sections && template.sections.length > 0) {
            this.collectLineItemResponses(template.sections, responses);
        }

        // De-duplicate by lineItemId (last value wins) to prevent duplicate inserts on save.
        const dedupedResponses = Array.from(
            new Map(responses.map((response) => [response.lineItemId, response])).values()
        );

        // Get status from templateStatusData if available
        const templateStatus = this.templateStatusData[template.templateId];
        const statusToSave = (templateStatus?.status !== undefined && templateStatus?.status !== null)
            ? templateStatus.status
            : template.status;

        return {
            blockScheduleId: this.isProjectFormMode ? null : this.skidData?.blockScheduleId,
            projectFormId: this.isProjectFormMode ? this.projectFormId : null,
            templateId: template.templateId,
            dueDate: template.dueDate,
            status: statusToSave,
            projectId: this.projectId,
            responses: dedupedResponses
        };

    }

    private clearDirtyLineItems(responses: Array<{ lineItemId: number }>): void {
        responses.forEach(response => this.dirtyLineItemIds.delete(response.lineItemId));
    }

    private clearDirtyLineItemsForTemplate(templateId: number): void {
        const template = this.templates.find(t => t.templateId === templateId);
        if (!template?.sections) {
            return;
        }

        const lineItemIds = new Set<number>();
        const collectLineItemIds = (sections: TemplateSectionModel[]): void => {
            sections.forEach(section => {
                section.lineItemList?.forEach(lineItem => lineItemIds.add(lineItem.lineItemId));
                if (section.subsections && section.subsections.length > 0) {
                    collectLineItemIds(section.subsections);
                }
            });
        };

        collectLineItemIds(template.sections);
        lineItemIds.forEach(lineItemId => this.dirtyLineItemIds.delete(lineItemId));
    }

    /**
     * Recursively collect file upload tasks from sections and subsections
     */
    private collectFileUploadTasks(
        sections: TemplateSectionModel[],
        scheduleId: number,
        uploadTasks: Promise<void>[],
        counters: { totalFiles: number; uploadedFiles: number; failedFiles: number }
    ): void {
        sections.forEach(section => {
            // Process file uploads in current section
            section.lineItemList?.forEach(lineItem => {
                // Check if this is an attachment, picture, or signature control
                if (lineItem.controlTypeId === ControlType.Attachment || lineItem.controlTypeId === ControlType.Picture || lineItem.controlTypeId === ControlType.Signature) {
                    const value = lineItem.lineItemId ? this.checklistData[lineItem.lineItemId] as FileControlValue : undefined;

                    if (value && value.newFiles && value.newFiles.length > 0) {
                        // Filter to only include actual File objects (exclude any serialized objects from localStorage)
                        const validFiles = value.newFiles.filter(f => f instanceof File);

                        if (validFiles.length > 0) {
                            const responseId = this.responseIdMap[lineItem.lineItemId];

                            if (responseId) {
                                counters.totalFiles += validFiles.length;

                                // Create upload task
                                const uploadTask = this.projecttemplateService.uploadFile(scheduleId, responseId, validFiles)
                                    .toPromise()
                                    .then(async (response) => {
                                        console.log('Files uploaded for lineItem', lineItem.lineItemId, response);
                                        counters.uploadedFiles += response?.filesUploaded || 0;
                                        await this.finalizeUploadedFiles(
                                            lineItem.lineItemId,
                                            scheduleId,
                                            responseId,
                                            validFiles,
                                            response?.fileIds || []
                                        );
                                    })
                                    .catch((error) => {
                                        console.error('Error uploading files for lineItem', lineItem.lineItemId, error);
                                        counters.failedFiles += validFiles.length;
                                    });

                                uploadTasks.push(uploadTask);
                            }
                        }
                    }
                }
            });

            // Recursively process subsections
            if (section.subsections && section.subsections.length > 0) {
                this.collectFileUploadTasks(section.subsections, scheduleId, uploadTasks, counters);
            }
        });
    }

    private async finalizeUploadedFiles(
        lineItemId: number,
        scheduleId: number,
        responseId: number,
        uploadedFiles: File[],
        uploadedFileIds: number[]
    ): Promise<void> {
        const currentValue = this.checklistData[lineItemId] as FileControlValue || { newFiles: [], existingFileIds: [] };
        const uploadedFileSet = new Set(uploadedFiles);
        const remainingNewFiles = (currentValue.newFiles || []).filter(file => !(file instanceof File && uploadedFileSet.has(file)));
        const mergedExistingFileIds = Array.from(new Set([
            ...(currentValue.existingFileIds || []),
            ...uploadedFileIds
        ]));

        this.checklistData[lineItemId] = {
            newFiles: remainingNewFiles,
            existingFileIds: mergedExistingFileIds
        };

        try {
            const files = await this.projecttemplateService.getFiles(scheduleId, responseId).toPromise();
            if (files) {
                this.existingFilesMap[lineItemId] = files;
                this.checklistData[lineItemId] = {
                    newFiles: remainingNewFiles,
                    existingFileIds: files.map(file => file.id)
                };
            }
        } catch (error) {
            console.error('Error refreshing uploaded files for lineItem', lineItemId, error);
        }

        this.cdr.markForCheck();
    }

    private async uploadFiles(templateId?: number): Promise<{ totalFiles: number; uploadedFiles: number; failedFiles: number }> {
        const counters = {
            totalFiles: 0,
            uploadedFiles: 0,
            failedFiles: 0
        };

        // For project forms, use projectFormId; for block schedules, use blockScheduleId
        const scheduleId = this.isProjectFormMode ? this.projectFormId : this.skidData?.blockScheduleId;
        if (!scheduleId) {
            return counters;
        }

        // Filter templates based on templateId parameter
        const templatesToProcess = templateId
            ? this.templates.filter(t => t.templateId === templateId)
            : this.templates;

        // Collect all file upload tasks
        const uploadTasks: Promise<void>[] = [];

        templatesToProcess.forEach(template => {
            if (template.sections && template.sections.length > 0) {
                this.collectFileUploadTasks(template.sections, scheduleId, uploadTasks, counters);
            }
        });

        // Wait for all uploads to complete
        await Promise.all(uploadTasks);

        return counters;
    }

    /**
     * Recursively validate line items in sections and subsections
     */
    private validateSections(sections: TemplateSectionModel[], errors: string[]): void {
        sections.forEach(section => {
            // Validate line items in current section
            section.lineItemList?.forEach(lineItem => {
                // Skip LabelText controls - they are display-only
                if (lineItem.controlTypeId === ControlType.LabelText) {
                    return;
                }

                const value = this.checklistData[lineItem.lineItemId];
                const lineItemErrors = this.validationService.validate(lineItem, value);
                lineItemErrors.forEach(error => {
                    if (error === 'This field is required') {
                        errors.push(`${lineItem.name} is required`);
                    } else {
                        errors.push(`${lineItem.name}: ${error}`);
                    }
                });

                // Optional empty tables are allowed, but once rows exist their required columns must be complete.
                if (lineItem.controlTypeId === ControlType.Table) {
                    const tableData = this.checklistData[lineItem.lineItemId] as any[];
                    const columns = lineItem.lineItemTableColumnList || [];
                    const requiredColumns = columns.filter(col => col.isRequired);

                    console.log('Table validation:', {
                        lineItemName: lineItem.name,
                        lineItemId: lineItem.lineItemId,
                        tableData: tableData,
                        columns: columns,
                        requiredColumns: requiredColumns
                    });

                    if (requiredColumns.length > 0 && tableData && tableData.length > 0) {
                        tableData.forEach((row, rowIndex) => {
                            requiredColumns.forEach(column => {
                                const key = `col_${column.columnId}`;
                                const cellValue = row[key];

                                // Check if cell value is empty based on data type
                                const isEmpty = this.isTableCellValueEmpty(cellValue, column.dataTypeId);
                                console.log(`Checking row ${rowIndex + 1}, column "${column.columnName}":`, {
                                    key,
                                    cellValue,
                                    dataTypeId: column.dataTypeId,
                                    isEmpty
                                });
                                if (isEmpty) {
                                    errors.push(`${lineItem.name} - Row ${rowIndex + 1}: "${column.columnName}" is required`);
                                }
                            });
                        });
                    }
                }
            });

            // Recursively validate subsections
            if (section.subsections && section.subsections.length > 0) {
                this.validateSections(section.subsections, errors);
            }
        });
    }

    private validateForm(templateId?: number): string[] {
        const errors: string[] = [];

        // Filter templates based on templateId parameter
        const templatesToValidate = templateId
            ? this.templates.filter(t => t.templateId === templateId)
            : this.templates;

        // Loop through templates and recursively validate their sections and subsections
        templatesToValidate.forEach(template => {
            const status = this.getTemplateStatus(template.templateId);
            if (!status.assigneedBy) {
                errors.push('Assignee is required');
            }
            if (!status.date) {
                errors.push('Date is required');
            }
            if (!status.duedate) {
                errors.push('Due Date is required');
            }

            if (template.sections && template.sections.length > 0) {
                this.validateSections(template.sections, errors);
            }
        });

        return errors;
    }

    private isTableCellValueEmpty(value: any, dataTypeId?: number): boolean {
        if (value === null || value === undefined) {
            return true;
        }

        // DataType enum: ShortText=1, LongText=2, Integer=3, Decimal=4, Date=5, CheckBox=6
        switch (dataTypeId) {
            case 1: // ShortText
            case 2: // LongText
                return typeof value === 'string' && value.trim() === '';
            case 3: // Integer
            case 4: // Decimal
                return value === 0 || value === '';
            case 5: // Date
                return !value;
            case 6: // CheckBox
                return false; // Checkboxes are never considered empty (they're boolean)
            case 8: // Tasks
                return !value || Number(value) <= 0;
            case 9: // Users
                return !value || !Array.isArray(value) || value.length === 0;
            case 11: // Skids
                if (value === null || value === undefined || value === '') {
                    return true;
                }
                const parsedSkidValue = Number(value);
                return !Number.isFinite(parsedSkidValue) || !(parsedSkidValue === -1 || parsedSkidValue > 0);
            default:
                return !value;
        }
    }

    private getStatusLabelFromNumber(status: number | undefined): string {
        if (status === undefined || status === null) {
            return 'Draft';
        }

        switch (status) {
            case ProjectTemplateStatus.Draft:
                return 'Draft';
            case ProjectTemplateStatus.Submitted:
                return 'Submitted';
            case ProjectTemplateStatus.Verified:
                return 'Verified';
            default:
                return 'Draft';
        }
    }

    private getStatusNumberFromLabel(statusLabel: string | undefined): number {
        if (!statusLabel) {
            return ProjectTemplateStatus.Draft;
        }

        switch (statusLabel.toLowerCase()) {
            case 'draft':
                return ProjectTemplateStatus.Draft;
            case 'submitted':
                return ProjectTemplateStatus.Submitted;
            case 'verified':
                return ProjectTemplateStatus.Verified;
            default:
                return ProjectTemplateStatus.Draft;
        }
    }

    private syncCurrentProjectFormStatus(status: ProjectTemplateSkidStatusModel | undefined): void {
        if (!this.currentProjectForm || !status) {
            return;
        }

        this.currentProjectForm = {
            ...this.currentProjectForm,
            status: this.getStatusLabelFromNumber(status.status),
            assignedBy: status.assigneedBy,
            date: this.parseStatusDate(status.date),
            duedate: this.parseStatusDate(status.duedate)
        };
        this.syncTemplateAccessState();
    }

    private refreshProjectFormAudit(templateId: number, complete?: () => void): void {
        if (!this.projectFormId) {
            complete?.();
            return;
        }

        this.projectFormService.getProjectFormById(this.projectFormId).pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: (projectForm) => {
                this.currentProjectForm = projectForm;
                this.loadTemplateStatus(templateId);
                complete?.();
            },
            error: (error) => {
                console.error('Unable to refresh project form audit information:', error);
                complete?.();
            }
        });
    }

    private shouldTriggerEwmrEmail(templateId: number, previousStatus?: number): boolean {
        if (!this.isProjectFormMode || !this.projectFormId || !templateId) {
            return false;
        }

        const previous = previousStatus ?? this.getStatusNumberFromLabel(this.currentProjectForm?.status);
        const nextStatus = this.templateStatusData[templateId]?.status ?? previous;

        return previous !== nextStatus && nextStatus === ProjectTemplateStatus.Submitted;
    }

    private saveTemplateStatusAndNavigate(templateId: number, recordsSaved: number, totalFiles: number, uploadedFiles: number, failedFiles: number): void {
        const status = this.templateStatusData[templateId];
        if (!status) {
            this.completeSaveAndNavigate(recordsSaved, totalFiles, uploadedFiles, failedFiles, templateId);
            return;
        }

        if (this.isProjectFormMode) {
            // Project Form Mode: Call UpdateProjectFormStatus endpoint
            if (!this.currentProjectForm || !this.currentProjectForm.templateNameId) {
                console.error('Project form data not available');
                this.completeSaveAndNavigate(recordsSaved, totalFiles, uploadedFiles, failedFiles, templateId);
                return;
            }

            // Convert number status to string for ProjectFormStatusModel
            const statusString = this.getStatusLabelFromNumber(status.status);

            const projectFormStatusModel = {
                status: statusString,
                assigneedBy: status.assigneedBy,
                date: this.parseStatusDate(status.date),
                duedate: this.parseStatusDate(status.duedate)
            };

            const templateNameId = parseInt(this.currentProjectForm.templateNameId);
            const previousStatus = this.getStatusNumberFromLabel(this.currentProjectForm.status);

            this.projectFormService.updateProjectFormStatus(this.projectFormId, templateNameId, projectFormStatusModel).pipe(
                takeUntil(this.destroy$)
            ).subscribe({
                next: () => {
                    console.log('Project form status saved successfully');
                    this.refreshProjectFormAudit(templateId, () => {
                        this.completeSaveAndNavigate(recordsSaved, totalFiles, uploadedFiles, failedFiles, templateId, previousStatus);
                    });
                },
                error: (error) => {
                    console.error('Error saving project form status:', error);
                    // Still navigate even if status save fails
                    this.completeSaveAndNavigate(recordsSaved, totalFiles, uploadedFiles, failedFiles, templateId);
                }
            });
        } else {
            // Block Schedule Mode: Call UpsertTemplateSkidStatus endpoint
            const shouldPersistOverride = this.shouldPersistOverride(templateId);
            const statusString = this.getStatusLabelFromNumber(status.status);

            const statusToSave = {
                ...status,
                isOverride: shouldPersistOverride,
                status: statusString
            } as any; // Backend expects string status, frontend uses number

            this.projecttemplateService.upsertTemplateSkidStatus(statusToSave).pipe(
                takeUntil(this.destroy$)
            ).subscribe({
                next: () => {
                    console.log('Template status saved successfully');
                    this.completeSaveAndNavigate(recordsSaved, totalFiles, uploadedFiles, failedFiles, templateId);
                },
                error: (error) => {
                    console.error('Error saving template status:', error);
                    // Still navigate even if status save fails
                    this.completeSaveAndNavigate(recordsSaved, totalFiles, uploadedFiles, failedFiles, templateId);
                }
            });
        }
    }

    private completeSaveAndNavigate(recordsSaved: number, totalFiles: number, uploadedFiles: number, failedFiles: number, templateId: number, previousProjectFormStatus?: number): void {
        this.saving = false;
        this.cdr.markForCheck();

        // Clear browser draft cache for this template after successful save
        // so latest server/synced data is shown on next open.
        this.clearStoredDraft(templateId);
        if (failedFiles === 0) {
            this.clearDirtyLineItemsForTemplate(templateId);
            this.dirtyTemplateIds.delete(templateId);
        }

        if (failedFiles === 0) {
            this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: `Checklist saved successfully. ${recordsSaved} record(s) saved.${totalFiles > 0 ? ` ${uploadedFiles} file(s) uploaded.` : ''}`
            });
        } else {
            this.messageService.add({
                severity: 'warn',
                summary: 'Partial Success',
                detail: `Checklist saved. ${uploadedFiles} of ${totalFiles} file(s) uploaded. ${failedFiles} failed.`
            });
        }

        const shouldSendImpactedAreaNotification = this.pendingImpactedAreaNotificationTemplateId === templateId;
        const completionMode = this.saveCompletionMode;
        this.pendingImpactedAreaNotificationTemplateId = null;
        this.saveCompletionMode = 'navigate';

        const completeInPlace = () => {
            if (this.isProjectFormMode) {
                this.syncCurrentProjectFormStatus(this.templateStatusData[templateId]);
            } else {
                this.loadTemplateStatus(templateId);
            }
            this.initializeUpdateMenu();
            this.cdr.markForCheck();
        };

        const completeNavigation = () => {
            if (completionMode === 'stay') {
                if (this.isProjectFormMode && this.shouldTriggerEwmrEmail(templateId, previousProjectFormStatus)) {
                    this.projecttemplateService.sendEwmrEmail(this.projectFormId, templateId).subscribe({
                        next: () => completeInPlace(),
                        error: (error) => {
                            console.error('EWMR email trigger failed:', error);
                            completeInPlace();
                        }
                    });
                    return;
                }
                completeInPlace();
                return;
            }

            if (this.isProjectFormMode) {
                if (this.shouldTriggerEwmrEmail(templateId, previousProjectFormStatus)) {
                    this.projecttemplateService.sendEwmrEmail(this.projectFormId, templateId).subscribe({
                        next: () => {
                            this.syncCurrentProjectFormStatus(this.templateStatusData[templateId]);
                            this.goBack();
                        },
                        error: (error) => {
                            console.error('EWMR email trigger failed:', error);
                            this.goBack();
                        }
                    });
                    return;
                }
                this.goBack();
            } else {
                this.router.navigate(['/equipment', this.projectId]);
            }
        };

        if (!shouldSendImpactedAreaNotification) {
            completeNavigation();
            return;
        }

        const requestBlockScheduleId = this.isProjectFormMode ? undefined : this.skidData?.blockScheduleId;
        const requestProjectFormId = this.isProjectFormMode ? this.projectFormId : undefined;

        this.projecttemplateService
            .sendImpactedAreaNotification(this.projectId, templateId, requestBlockScheduleId, requestProjectFormId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    completeNavigation();
                },
                error: (error) => {
                    console.error('Impacted area notification trigger failed:', error);
                    completeNavigation();
                }
            });
    }

    onClose(): void {
        if (this.isProjectFormMode) {
            this.goBack();
        } else {
            this.router.navigate(['/equipment', this.projectId]);
        }
    }

    onTabChange(newValue: string | number | undefined): void {
        // Guard against undefined
        if (newValue === undefined) return;
        const nextIndex = parseInt(newValue.toString(), 10);
        const nextTemplate = this.templates[nextIndex];
        if (nextTemplate?.templateId && this.isTemplateLocked(nextTemplate.templateId)) {
            return;
        }

        // Save current tab data before switching (if needed)
        const previousTemplate = this.templates[this.activeTabIndex];
        if (previousTemplate) {
            this.saveCurrentTemplateToStorage(previousTemplate.templateId);
        }

        // PrimeNG 20 tabs valueChange event provides the new active tab value directly
        const valueStr = newValue.toString();
        this.activeTabValue = valueStr;
        this.activeTabIndex = nextIndex;
        console.log('Tab changed to value:', this.activeTabValue, 'index:', this.activeTabIndex);
        this.initializeUpdateMenu();
        this.cdr.markForCheck();
    }

    trackByTemplateId(_index: number, template: ProjectTemplateModel): number {
        return template.templateId;
    }

    getEquipmentDisplayTitle(): string {
        const savedName = this.normalizeDisplayText(this.skidData?.name || this.skidData?.blockName);
        if (savedName) {
            return savedName;
        }

        const configuredPrefix = this.normalizeDisplayText(this.projectData?.prefix);
        const prefix = !configuredPrefix || configuredPrefix.toLowerCase() === 'skid'
            ? 'MVPS'
            : configuredPrefix;
        return `${prefix} #${this.skidData?.tmeicSkidNo ?? ''}`.trim();
    }

    getProjectFormDisplayTitle(): string {
        const form = this.currentProjectForm;
        const template = this.templates?.[this.activeTabIndex] || this.templates?.[0];

        const templateName = this.normalizeDisplayText(
            form?.shortCode ||
            form?.templateShortCode ||
            form?.templateName ||
            template?.shortCode ||
            template?.templateName ||
            'Form'
        );
        const templateNameId = this.normalizeDisplayText(form?.templateNameId);
        const formName = this.normalizeDisplayText(form?.name);
        const templateLabel = templateNameId ? `${templateName} #${templateNameId}` : templateName;

        return formName ? `${templateLabel} - ${formName}` : templateLabel;
    }

    private normalizeDisplayText(value: unknown): string {
        return value === null || value === undefined ? '' : String(value).trim();
    }

    trackBySectionId(_index: number, section: any): number {
        return section.sectionId;
    }

    trackByLineItemId(_index: number, lineItem: LineItemModel): number {
        return lineItem.lineItemId;
    }

    // Template Skid Status Methods
    loadProjectUsers(): void {
        this.userService.getProjectUserList(this.projectId).pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: (users) => {
                this.projectUsers = users.map(u => ({
                    label: `${u.firstName} ${u.lastName}`,
                    value: u.userId
                }));
                this.cdr.markForCheck();
            },
            error: (error) => {
                console.error('Error loading project users:', error);
            }
        });
    }

    loadTemplateStatus(templateId: number): void {
        if (this.isProjectFormMode) {
            // Project Form Mode: Populate status from currentProjectForm data (already fetched)
            if (this.currentProjectForm) {
                this.templateStatusData[templateId] = {
                    blockScheduleId: 0, // Not used in project form mode
                    projectTemplateId: templateId,
                    status: this.getStatusNumberFromLabel(this.currentProjectForm.status),
                    isOverride: false,
                    assigneedBy: this.currentProjectForm.assignedBy,
                    date: this.parseStatusDate(this.currentProjectForm.date),
                    duedate: this.parseStatusDate(this.currentProjectForm.duedate),
                    createdBy: this.currentProjectForm.createdBy,
                    createdAt: this.currentProjectForm.createdAt,
                    updatedBy: this.currentProjectForm.updatedBy,
                    updatedAt: this.currentProjectForm.updatedAt,
                    createdByName: this.currentProjectForm.createdByName,
                    updatedByName: this.currentProjectForm.updatedByName
                };
                this.statusFieldSelection[templateId] = this.templateStatusData[templateId].status;
                this.statusFieldVisible[templateId] = true;
                this.overrideSelection[templateId] = !!this.templateStatusData[templateId].isOverride;
                this.syncTemplateAccessState();
                this.cdr.markForCheck();
            }
        } else {
            // Block Schedule Mode: Load status from API
            const blockScheduleId = this.skidData?.blockScheduleId;
            if (!blockScheduleId) return;

            this.projecttemplateService.getTemplateSkidStatus(blockScheduleId, templateId).pipe(
                takeUntil(this.destroy$)
            ).subscribe({
                next: (status) => {
                    if (status) {
                        // Convert string status from backend to number for frontend
                        const statusNumber = this.getStatusNumberFromLabel(status.status as any);

                        // Convert date strings to Date objects for the datepicker
                        this.templateStatusData[templateId] = {
                            ...status,
                            status: statusNumber,
                            isOverride: !!status.isOverride,
                            date: this.parseStatusDate(status.date),
                            duedate: this.parseStatusDate(status.duedate)
                        } as any;
                    } else {
                        // Initialize empty status if none exists
                        this.templateStatusData[templateId] = {
                            blockScheduleId: blockScheduleId,
                            projectTemplateId: templateId,
                            status: ProjectTemplateStatus.Draft,
                            isOverride: false
                        };
                    }
                    this.loadedTemplateStatusIds.add(templateId);
                    this.statusFieldSelection[templateId] = this.templateStatusData[templateId].status;
                    this.statusFieldVisible[templateId] = true;
                    this.overrideSelection[templateId] = !!this.templateStatusData[templateId].isOverride;
                    this.syncTemplateAccessState();
                    this.initializeUpdateMenu();
                    if (this.shouldDeferRequestedTemplateFallback()) {
                        this.applyRequestedActiveTab();
                    } else {
                        this.ensureActiveTemplateAccessible();
                    }
                    this.cdr.markForCheck();
                },
                error: (error) => {
                    console.error('Error loading template status:', error);
                }
            });
        }
    }

    onStatusFieldChange(templateId: number, field: keyof ProjectTemplateSkidStatusModel, value: any): void {
        if (field === 'status' && !this.canEditStatusField(templateId)) {
            return;
        }

        if ((field === 'assigneedBy' || field === 'date' || field === 'duedate') && !this.canEditTemplateDetails(templateId)) {
            return;
        }

        if (this.isReadOnly) {
            return;
        }

        if (field === 'status') {
            const previousStatus = this.getSelectedStatusValue(templateId);
            this.statusFieldSelection[templateId] = value;

            if (!this.canProceedWithStatus(templateId, value)) {
                this.restorePreviousStatusSelection(templateId, previousStatus);
                return;
            }
        }

        if (this.isProjectFormMode) {
            // Project Form Mode: blockScheduleId not used (set to 0)
            // Initialize status object if it doesn't exist
            if (!this.templateStatusData[templateId]) {
                this.templateStatusData[templateId] = {
                    blockScheduleId: 0,
                    projectTemplateId: templateId
                };
            }

            // Update the field
            (this.templateStatusData[templateId] as any)[field] = value;
            if (field === 'status') {
                this.statusFieldSelection[templateId] = value;
                this.templateStatusData[templateId].isOverride = false;
            }

            this.onSave(true, field === 'status' && this.shouldTriggerImpactedAreaNotification(value), 'stay');
        } else {
            // Block Schedule Mode: Requires blockScheduleId
            const blockScheduleId = this.skidData?.blockScheduleId;
            if (!blockScheduleId) return;

            // Initialize status object if it doesn't exist
            if (!this.templateStatusData[templateId]) {
                this.templateStatusData[templateId] = {
                    blockScheduleId: blockScheduleId,
                    projectTemplateId: templateId
                };
            }

            // Update the field
            (this.templateStatusData[templateId] as any)[field] = value;
            if (field === 'status') {
                this.statusFieldSelection[templateId] = value;
                this.templateStatusData[templateId].isOverride = this.shouldPersistOverride(templateId);
            }

            this.onSave(true, field === 'status' && this.shouldTriggerImpactedAreaNotification(value), 'stay');
        }
    }

    private shouldTriggerImpactedAreaNotification(status: ProjectTemplateStatus): boolean {
        return status === ProjectTemplateStatus.Submitted;
    }

    private saveTemplateStatus(templateId: number): void {
        const status = this.templateStatusData[templateId];
        if (!status) return;

        if (this.isProjectFormMode) {
            // Project Form Mode: Call UpdateProjectFormStatus endpoint
            if (!this.currentProjectForm || !this.currentProjectForm.templateNameId) {
                console.error('Project form data not available');
                return;
            }

            // Convert number status to string for ProjectFormStatusModel
            const statusString = this.getStatusLabelFromNumber(status.status);

            // Prepare the model for project form status update
            const projectFormStatusModel = {
                status: statusString,
                assigneedBy: status.assigneedBy,
                date: this.parseStatusDate(status.date),
                duedate: this.parseStatusDate(status.duedate)
            };

            const templateNameId = parseInt(this.currentProjectForm.templateNameId);

            this.projectFormService.updateProjectFormStatus(this.projectFormId, templateNameId, projectFormStatusModel).pipe(
                takeUntil(this.destroy$)
            ).subscribe({
                next: () => {
                    console.log('Project form status saved successfully');
                    this.refreshProjectFormAudit(templateId);
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Status updated successfully'
                    });
                },
                error: (error) => {
                    console.error('Error saving project form status:', error);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to save project form status'
                    });
                }
            });
        } else {
            // Block Schedule Mode: Call UpsertTemplateSkidStatus endpoint
            const shouldPersistOverride = this.shouldPersistOverride(templateId);
            const statusString = this.getStatusLabelFromNumber(status.status);

            // Convert Date objects to ISO strings for API
            const statusToSave = {
                ...status,
                isOverride: shouldPersistOverride,
                status: statusString
            } as any; // Backend expects string status, frontend uses number

            this.projecttemplateService.upsertTemplateSkidStatus(statusToSave).pipe(
                takeUntil(this.destroy$)
            ).subscribe({
                next: () => {
                    console.log('Template status saved successfully');
                    // Reload to get updated timestamps and created by info
                    this.loadTemplateStatus(templateId);
                },
                error: (error) => {
                    console.error('Error saving template status:', error);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to save template status'
                    });
                }
            });
        }
    }

    private statusRequiresValidation(status: number | undefined | null): boolean {
        return status === ProjectTemplateStatus.Submitted || status === ProjectTemplateStatus.Verified;
    }

    private canProceedWithStatus(templateId: number, selectedStatus: number | undefined | null): boolean {
        if (!this.statusRequiresValidation(selectedStatus)) {
            return true;
        }

        const validationErrors = this.validateForm(templateId);
        this.showErrors = validationErrors.length > 0;
        this.initializeUpdateMenu();
        this.cdr.markForCheck();

        if (validationErrors.length === 0) {
            return true;
        }

        const statusLabel = selectedStatus === ProjectTemplateStatus.Verified ? 'Verified' : 'Submitted';
        this.messageService.add({
            severity: 'error',
            summary: 'Validation Failed',
            detail: `Cannot save as ${statusLabel}. Please fill all required fields. ${validationErrors.length} field(s) missing.`
        });
        return false;
    }

    private restorePreviousStatusSelection(templateId: number, previousStatus: number | null | undefined): void {
        this.statusFieldSelection[templateId] = previousStatus;
        this.statusFieldVisible[templateId] = false;
        this.cdr.detectChanges();

        setTimeout(() => {
            this.statusFieldVisible[templateId] = true;
            this.cdr.markForCheck();
        });
    }

    getTemplateStatus(templateId: number): ProjectTemplateSkidStatusModel {
        return this.templateStatusData[templateId] || {
            blockScheduleId: this.skidData?.blockScheduleId || 0,
            projectTemplateId: templateId
        };
    }

    getSelectedStatusValue(templateId: number): number | null | undefined {
        if (Object.prototype.hasOwnProperty.call(this.statusFieldSelection, templateId)) {
            return this.statusFieldSelection[templateId];
        }

        return this.getTemplateStatus(templateId).status;
    }

    isStatusFieldVisible(templateId: number): boolean {
        return this.statusFieldVisible[templateId] !== false;
    }

    getTemplateStatusDate(templateId: number): Date | undefined {
        const status = this.getTemplateStatus(templateId);
        if (!status.date) return undefined;
        return this.parseStatusDate(status.date);
    }

    onDownloadPdf(template: ProjectTemplateModel): void {
        const contextId = this.isProjectFormMode ? this.projectFormId : this.skidData?.blockScheduleId;

        if (!contextId || !this.projectId || !template.templateId) {
            this.messageService.add({
                severity: 'error',
                summary: 'Download Failed',
                detail: 'Missing required data for PDF generation'
            });
            return;
        }

        this.downloading = true;
        this.cdr.markForCheck();

        const download$ = this.isProjectFormMode
            ? this.projecttemplateService.downloadChecklistPdfByProjectForm(contextId, this.projectId, template.templateId)
            : this.projecttemplateService.downloadChecklistPdf(contextId, this.projectId, template.templateId);

        download$
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (blob: Blob) => {
                    // Create download link and trigger download
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = this.isProjectFormMode
                        ? buildProjectFormPdfFileName(
                            this.currentProject,
                            this.currentProjectForm?.shortCode || this.currentProjectForm?.templateName || `Form${contextId}`,
                            this.currentProjectForm?.name || `Form${contextId}`
                        )
                        : buildEquipmentChecklistPdfFileName(
                            this.currentProject,
                            template.templateName,
                            this.skidData
                        );
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);

                    this.messageService.add({
                        severity: 'success',
                        summary: 'Download Complete',
                        detail: 'PDF downloaded successfully'
                    });

                    this.downloading = false;
                    this.cdr.markForCheck();
                },
                error: (error) => {
                    console.error('Error downloading PDF:', error);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Download Failed',
                        detail: 'An error occurred while generating the PDF'
                    });

                    this.downloading = false;
                    this.cdr.markForCheck();
                }
            });
    }

    private setActiveTabByTemplateId(templateId: number): void {
        // This will be called after templates are loaded
        // Find the index of the template with the given templateId
        const index = this.templates.findIndex(t => t.templateId === templateId);
        if (index !== -1) {
            this.activeTabIndex = index;
            this.activeTabValue = index.toString();
            console.log('Active tab set to index:', index, 'for templateId:', templateId);
            this.initializeUpdateMenu();
            this.cdr.markForCheck();
        } else {
            console.warn('Template with ID', templateId, 'not found in templates list');
        }
    }

    private applyRequestedActiveTab(): void {
        if (this.requestedTemplateId) {
            this.setActiveTabByTemplateId(this.requestedTemplateId);
        }
    }

    private shouldDeferRequestedTemplateFallback(): boolean {
        if (this.isProjectFormMode || !this.requestedTemplateId) {
            return false;
        }

        return this.loadedTemplateStatusIds.size < this.templates.length;
    }

    // Task Management Methods
    prepareTaskMenu(section: any, template: ProjectTemplateModel): void {
        const counts = this.getSectionTaskCounts(section.sectionId, template.templateId);
        const items: MenuItem[] = [];

        if (this.canCreateTasks) {
            items.push({
                label: 'Create Task',
                icon: 'pi pi-plus',
                disabled: this.isProjectInactive,
                command: () => this.openCreateTaskDialog(section, template)
            });
        }

        if (this.canViewTasks) {
            items.push({
                label: `View Tasks [${counts.open} | ${counts.completed}]`,
                icon: 'pi pi-list',
                command: () => this.openViewTasksDialog(section, template)
            });
        }

        this.taskMenuItems = items;
    }

    openCreateTaskDialog(section: any, template: ProjectTemplateModel): void {
        if (this.isProjectInactive || !this.canCreateTasks) {
            return;
        }
        this.selectedSectionForTask = section;
        this.selectedTemplateForTask = template;
        this.showTaskFormDialog = true;
        this.cdr.markForCheck();
    }

    openViewTasksDialog(section: any, template: ProjectTemplateModel): void {
        if (!this.canViewTasks) {
            return;
        }
        this.selectedSectionForTask = section;
        this.selectedTemplateForTask = template;
        this.loadingTasks = true;
        this.showViewTasksDialog = true;
        this.cdr.markForCheck();

        // Load tasks for this section using blockScheduleId with filter
        const filter: TaskFilter = {
            sectionId: section.sectionId,
            projectTemplateId: template.templateId
        };

        if (this.blockScheduleId > 0) {
            this.taskManagementService.getTasksByBlockSchedule(this.blockScheduleId, filter)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (tasks: ProjectTask[]) => {
                        this.sectionTasks = tasks || [];
                        this.loadingTasks = false;
                        this.cdr.markForCheck();
                    },
                    error: (err: Error) => {
                        console.error('Error loading section tasks:', err);
                        this.sectionTasks = [];
                        this.loadingTasks = false;
                        this.cdr.markForCheck();
                    }
                });
        } else {
            // For project-level tasks (no blockScheduleId), use template-based query
            this.taskManagementService.getTasksByTemplate(template.templateId)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (tasks: ProjectTask[]) => {
                        // Filter by sectionId on client side
                        this.sectionTasks = (tasks || []).filter(t => t.sectionId === section.sectionId);
                        this.loadingTasks = false;
                        this.cdr.markForCheck();
                    },
                    error: (err: Error) => {
                        console.error('Error loading section tasks:', err);
                        this.sectionTasks = [];
                        this.loadingTasks = false;
                        this.cdr.markForCheck();
                    }
                });
        }
    }

    onTaskFormClose(): void {
        this.showTaskFormDialog = false;
        this.selectedSectionForTask = null;
        this.selectedTemplateForTask = null;
        this.editTaskForDialog = undefined;
        this.cdr.markForCheck();
    }

    openEditTaskDialog(task: ProjectTask): void {
        const template = this.selectedTemplateForTask
            || this.templates.find(t => Number(t.templateId) === Number(task.projectTemplateId))
            || null;
        const section = this.selectedSectionForTask
            || template?.sections?.find(s => Number(s.sectionId) === Number(task.sectionId))
            || null;

        this.editTaskForDialog = {
            ...task,
            templateShortCode: task.templateShortCode || template?.shortCode,
            templateName: task.templateName || template?.templateName,
            sectionName: task.sectionName || section?.sectionName
        };
        this.selectedSectionForTask = section || { sectionId: task.sectionId };
        this.selectedTemplateForTask = template || { templateId: task.projectTemplateId } as ProjectTemplateModel;
        this.showViewTasksDialog = false;
        this.showTaskFormDialog = true;
        this.cdr.markForCheck();
    }

    onTaskSubmit(taskData: TaskCreateUpdate): void {
        if (this.isReadOnly) return;
        // Add section and template context to the task
        const taskWithContext: TaskCreateUpdate = {
            ...taskData,
            projectId: this.projectId,
            blockScheduleId: this.blockScheduleId || 0,
            sectionId: this.selectedSectionForTask?.sectionId,
            projectTemplateId: this.selectedTemplateForTask?.templateId
        };

        // Check if we're editing an existing task
        if (this.editTaskForDialog?.taskId) {
            if (!this.canUpdateTasks) {
                return;
            }
            // Update existing task
            this.taskManagementService.updateTask(this.editTaskForDialog.taskId, taskWithContext).pipe(
                takeUntil(this.destroy$)
            ).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Task updated successfully'
                    });
                    this.showTaskFormDialog = false;
                    this.selectedSectionForTask = null;
                    this.selectedTemplateForTask = null;
                    this.editTaskForDialog = undefined;
                    this.refreshTaskCounts();
                    this.cdr.markForCheck();
                },
                error: (err: Error) => {
                    console.error('Error updating task:', err);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update task'
                    });
                }
            });
        } else {
            if (!this.canCreateTasks) {
                return;
            }
            // Create new task
            this.taskManagementService.createTask(taskWithContext).pipe(
                takeUntil(this.destroy$)
            ).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Task created successfully'
                    });
                    this.showTaskFormDialog = false;
                    this.selectedSectionForTask = null;
                    this.selectedTemplateForTask = null;
                    this.editTaskForDialog = undefined;
                    this.refreshTaskCounts();
                    this.cdr.markForCheck();
                },
                error: (err: Error) => {
                    console.error('Error creating task:', err);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create task'
                    });
                }
            });
        }
    }

    onViewTasksDialogClose(): void {
        this.showViewTasksDialog = false;
        this.selectedSectionForTask = null;
        this.selectedTemplateForTask = null;
        this.sectionTasks = [];
        this.cdr.markForCheck();
    }

    /**
     * Get task counts for a specific section
     */
    getSectionTaskCounts(sectionId: number, templateId: number): { total: number; open: number; completed: number } {
        const key = `${sectionId}_${templateId}`;
        return this.sectionTaskCounts[key] || { total: 0, open: 0, completed: 0 };
    }

    getTemplateTaskCounts(templateId: number): { total: number; open: number; completed: number } {
        return Object.entries(this.sectionTaskCounts)
            .filter(([key]) => {
                const [, entryTemplateId] = key.split('_');
                return Number(entryTemplateId) === templateId;
            })
            .reduce(
                (acc, [, counts]) => ({
                    total: acc.total + counts.total,
                    open: acc.open + counts.open,
                    completed: acc.completed + counts.completed
                }),
                { total: 0, open: 0, completed: 0 }
            );
    }

    getTemplateOpenTaskCount(templateId: number): number {
        return this.getTemplateTaskCounts(templateId).open;
    }

    /**
     * Load all tasks for the current blockSchedule and calculate counts per section
     */
    private loadSectionTaskCounts(): void {
        if (!this.blockScheduleId || this.blockScheduleId <= 0) {
            return;
        }

        this.taskManagementService.getTasksByBlockSchedule(this.blockScheduleId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (tasks: ProjectTask[]) => {
                    this.allTasks = tasks || [];
                    this.calculateSectionTaskCounts();
                    this.cdr.markForCheck();
                },
                error: (err: Error) => {
                    console.error('Error loading tasks for counts:', err);
                    this.allTasks = [];
                    this.cdr.markForCheck();
                }
            });
    }

    /**
     * Calculate task counts per section from loaded tasks
     */
    private calculateSectionTaskCounts(): void {
        this.sectionTaskCounts = {};

        this.allTasks.forEach(task => {
            if (task.sectionId && task.projectTemplateId) {
                const key = `${task.sectionId}_${task.projectTemplateId}`;

                if (!this.sectionTaskCounts[key]) {
                    this.sectionTaskCounts[key] = { total: 0, open: 0, completed: 0 };
                }

                this.sectionTaskCounts[key].total++;

                const type = task.statusType?.toLowerCase();
                if (type === 'closed' || type === 'verified') {
                    this.sectionTaskCounts[key].completed++;
                } else {
                    this.sectionTaskCounts[key].open++;
                }
            }
        });
    }

    /**
     * Refresh task counts after task creation/update
     */
    refreshTaskCounts(): void {
        this.loadSectionTaskCounts();
    }

    /**
     * Build hierarchical structure from flat section list
     */
    private buildHierarchy(flatSections: TemplateSectionModel[]): TemplateSectionModel[] {
        const sectionMap = new Map<number, TemplateSectionModel>();
        const rootSections: TemplateSectionModel[] = [];

        // First pass: Create map of all sections with isExpanded: true
        flatSections.forEach(section => {
            sectionMap.set(section.sectionId!, { ...section, subsections: [], isExpanded: true });
        });

        // Second pass: Build parent-child relationships
        flatSections.forEach(section => {
            const sectionNode = sectionMap.get(section.sectionId!);
            if (sectionNode) {
                if (section.parentSectionId === null || section.parentSectionId === undefined) {
                    // Root level section
                    rootSections.push(sectionNode);
                } else {
                    // Child section - add to parent's subsections
                    const parent = sectionMap.get(section.parentSectionId);
                    if (parent) {
                        parent.subsections = parent.subsections || [];
                        parent.subsections.push(sectionNode);
                    } else {
                        // Parent not found, treat as root
                        rootSections.push(sectionNode);
                    }
                }
            }
        });

        // Sort sections by sortOrder
        this.sortSections(rootSections);
        return rootSections;
    }

    /**
     * Recursively sort sections and subsections by sortOrder
     */
    private sortSections(sections: TemplateSectionModel[]): void {
        sections.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        sections.forEach(section => {
            if (section.subsections && section.subsections.length > 0) {
                this.sortSections(section.subsections);
            }
        });
    }

    /**
     * Recursively sort line items within all sections
     */
    private sortLineItems(sections: TemplateSectionModel[]): void {
        sections.forEach(section => {
            if (section.lineItemList && section.lineItemList.length > 0) {
                section.lineItemList.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
            }
            if (section.subsections && section.subsections.length > 0) {
                this.sortLineItems(section.subsections);
            }
        });
    }

    /**
     * Toggle section expand/collapse
     */
    toggleSection(section: TemplateSectionModel): void {
        section.isExpanded = !section.isExpanded;
        this.cdr.markForCheck();
    }

    isTemplateControlReadOnly(templateId: number): boolean {
        return this.isReadOnly || this.getTemplateAccessState(templateId) !== 'editable';
    }

    isActiveTemplateReadOnly(): boolean {
        const activeTemplate = this.templates[this.activeTabIndex];
        return !activeTemplate || this.isTemplateControlReadOnly(activeTemplate.templateId);
    }

    canUseUpdateAndClose(templateId: number): boolean {
        return !this.isReadOnly && (this.getTemplateAccessState(templateId) === 'editable' || this.isProjectAdminReviewer());
    }

    canEditTemplateDetails(templateId: number): boolean {
        return !this.isReadOnly && this.getTemplateAccessState(templateId) === 'editable';
    }

    canEditStatusField(templateId: number): boolean {
        if (this.isReadOnly) {
            return false;
        }

        if (this.isProjectAdminReviewer()) {
            return true;
        }

        return this.getTemplateAccessState(templateId) === 'editable';
    }

    showOverrideCheckbox(templateId: number): boolean {
        if (this.isProjectFormMode || this.isReadOnly || !this.isProjectAdminReviewer()) {
            return false;
        }

        return true;
    }

    isOverridePersisted(templateId: number): boolean {
        return !!this.getTemplateStatus(templateId).isOverride;
    }

    onOverrideToggle(templateId: number, checked: boolean): void {
        if (this.isOverridePersisted(templateId)) {
            return;
        }

        this.overrideSelection[templateId] = checked;
        if (!this.templateStatusData[templateId]) {
            const blockScheduleId = this.skidData?.blockScheduleId || 0;
            this.templateStatusData[templateId] = {
                blockScheduleId,
                projectTemplateId: templateId,
                status: ProjectTemplateStatus.Draft,
                isOverride: false
            };
        }
        this.templateStatusData[templateId].isOverride = checked;
        this.syncTemplateAccessState();
        this.cdr.markForCheck();
    }

    getStatusOptionsForTemplate(templateId: number): { label: string; value: number }[] {
        const currentStatus = this.getTemplateStatus(templateId).status ?? ProjectTemplateStatus.Draft;

        if (this.isProjectAdminReviewer()) {
            return this.statusOptions;
        }

        if (this.getTemplateAccessState(templateId) === 'editable') {
            return this.statusOptions.filter(option =>
                option.value === ProjectTemplateStatus.Draft || option.value === ProjectTemplateStatus.Submitted
            );
        }

        return this.statusOptions.filter(option => option.value === currentStatus);
    }

    isTemplateLocked(templateId: number): boolean {
        return this.getTemplateAccessState(templateId) === 'locked';
    }

    getTemplateAccessState(templateId: number): TemplateAccessState {
        return this.templateAccessState[templateId] || (this.isProjectFormMode ? 'editable' : 'locked');
    }

    getTemplateLockMessage(templateId: number): string {
        return this.isTemplateLocked(templateId)
            ? 'Locked until the previous template is submitted or overridden.'
            : '';
    }

    private getFooterStatusOptions(templateId?: number): { label: string; value: number }[] {
        if (!templateId) {
            return [];
        }

        return this.getStatusOptionsForTemplate(templateId);
    }

    private syncTemplateAccessState(): void {
        if (this.isProjectFormMode) {
            this.templateAccessState = this.templates.reduce((acc, template) => {
                const status = this.getTemplateStatus(template.templateId).status ?? ProjectTemplateStatus.Draft;
                acc[template.templateId] = status === ProjectTemplateStatus.Draft ? 'editable' : 'readonly';
                return acc;
            }, {} as { [templateId: number]: TemplateAccessState });
            return;
        }

        this.templateAccessState = {};
        this.templates.forEach((template, index) => {
            const status = this.getTemplateStatus(template.templateId);
            const previousTemplate = index > 0 ? this.templates[index - 1] : undefined;
            const previousStatus = previousTemplate ? this.getTemplateStatus(previousTemplate.templateId) : undefined;
            const isUnlocked = index === 0 || (
                !!previousTemplate &&
                this.templateAccessState[previousTemplate.templateId] !== 'locked' &&
                (
                    (previousStatus?.status ?? ProjectTemplateStatus.Draft) !== ProjectTemplateStatus.Draft ||
                    !!previousStatus?.isOverride
                )
            );

            this.templateAccessState[template.templateId] = !isUnlocked
                ? 'locked'
                : (status.status ?? ProjectTemplateStatus.Draft) === ProjectTemplateStatus.Draft
                    ? 'editable'
                    : 'readonly';
        });
    }

    private ensureActiveTemplateAccessible(): void {
        const activeTemplate = this.templates[this.activeTabIndex];
        if (!activeTemplate) {
            return;
        }

        if (this.shouldDeferRequestedTemplateFallback()) {
            return;
        }

        if (!this.isTemplateLocked(activeTemplate.templateId)) {
            return;
        }

        const nextAccessibleIndex = this.templates.findIndex(template => this.getTemplateAccessState(template.templateId) !== 'locked');
        if (nextAccessibleIndex === -1) {
            return;
        }

        this.activeTabIndex = nextAccessibleIndex;
        this.activeTabValue = nextAccessibleIndex.toString();
        this.initializeUpdateMenu();
    }

    private shouldPersistOverride(templateId: number): boolean {
        if (this.isProjectFormMode) {
            return false;
        }

        return this.isProjectAdminReviewer()
            && (
                !!this.getTemplateStatus(templateId).isOverride ||
                !!this.overrideSelection[templateId]
            );
    }

    private isProjectAdminReviewer(): boolean {
        return this.permissionHelper.isProjectAdmin(this.currentProject);
    }

    formatTaskDate(date?: string | Date | null): string {
        if (!date) {
            return '';
        }

        return this.timezoneService.formatStoredDate(date) || '';
    }

    getTemplateLastUpdatedText(templateId: number): string | null {
        const status = this.getTemplateStatus(templateId);
        if (status.updatedAt) {
            const updatedBy = status.updatedByName?.trim() || 'Unknown user';
            return `${updatedBy} on ${this.formatAuditDateTime(status.updatedAt)}`;
        }

        if (this.isProjectFormMode && status.createdAt) {
            const createdBy = status.createdByName?.trim() || 'Unknown user';
            return `${createdBy} on ${this.formatAuditDateTime(status.createdAt)}`;
        }

        return null;
    }

    getTemplateAuditLabel(templateId: number): string {
        const status = this.getTemplateStatus(templateId);
        return status.updatedAt ? 'Last updated' : 'Created by';
    }

    private formatAuditDateTime(value: string | Date): string {
        const rawValue = String(value);
        const hasTimezone = /(?:Z|[+-]\d{2}:\d{2})$/i.test(rawValue);
        const date = new Date(value instanceof Date ? value : (hasTimezone ? rawValue : `${rawValue}Z`));

        if (Number.isNaN(date.getTime())) {
            return rawValue;
        }

        return date.toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    }
}
